import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabase';
import { fetchWeatherByLocation } from '../../utils/weatherApi';
import { WeatherState } from '../../types/weather';

export const addWeatherLocation = createAsyncThunk(
  'weather/addWeatherLocation',
  async (location: string, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Fetch weather data to validate location and get coordinates
      const weatherData = await fetchWeatherByLocation(location);

      const { data, error } = await supabase
        .from('weather_locations')
        .insert([{
          name: location,
          latitude: weatherData.forecast.location.lat,
          longitude: weatherData.forecast.location.lon,
          user_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      // Return both location and weather data
      return {
        location: { id: data.id, name: data.name },
        weather: weatherData
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchWeatherLocations = createAsyncThunk(
  'weather/fetchWeatherLocations',
  async (_, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('weather_locations')
        .select('id, name')
        .eq('user_id', user.id);

      if (error) throw error;
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchWeatherData = createAsyncThunk(
  'weather/fetchWeatherData',
  async (locationName: string, thunkAPI) => {
    try {
      const data = await fetchWeatherByLocation(locationName);
      return data;
    } catch (error: any) {
      console.error('Weather fetch error:', error);
      return thunkAPI.rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch weather data'
      );
    }
  }
);

const initialState: WeatherState = {
  current: null,
  forecast: null,
  locations: [],
  selectedLocation: null,
  loading: false,
  error: null
};

const weatherSlice = createSlice({
  name: 'weather',
  initialState,
  reducers: {
    setSelectedLocation: (state, action) => {
      state.selectedLocation = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWeatherData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeatherData.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload.current;
        state.forecast = action.payload.forecast;
        state.error = null;
      })
      .addCase(fetchWeatherData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addWeatherLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addWeatherLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.locations.push(action.payload.location);
        state.selectedLocation = action.payload.location.id;
        state.current = action.payload.weather.current;
        state.forecast = action.payload.weather.forecast;
        state.error = null;
      })
      .addCase(addWeatherLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchWeatherLocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeatherLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = action.payload;
        if (action.payload.length > 0 && !state.selectedLocation) {
          state.selectedLocation = action.payload[0].id;
        }
        state.error = null;
      })
      .addCase(fetchWeatherLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setSelectedLocation } = weatherSlice.actions;
export default weatherSlice.reducer;